import React, { useState } from 'react';
import {
  Grid2 as Grid,
  Stack,
  Card,
  CardContent,
  Typography,
  Button,
  Badge,
  Alert,
  Divider,
} from '@mui/material';
import {
  Checklist
} from '@mui/icons-material';
import { useParams, useHistory } from 'react-router-dom';
import Popover from 'react-text-selection-popover';

import { PageStatusDialog } from './pageStatusDialog.jsx';
import { EncounterHeaderInfo } from './encounterHeaderInfo.jsx';
import { EncounterNotes } from './encounterNotes.jsx';
import { EncounterLineItemList } from './encounterLineItemList.jsx';
import { CompletionDialog } from './completionDialog.jsx';
import { CommentSection } from './commentSection.jsx';
import { AddCommentDialog } from './addCommentDialog.jsx';
import { AcceptRejectDialog } from './acceptRejectDialog.jsx';
import CollapsableSection from '../../../components/collapsablesection/components/collapsablesection.js';
import PatientHistorySection from '../../../components/patienthistorysection/components/patienthistorysection.js';
import { GRID_ROW_FULL_WIDTH_ROW } from '../constants.js';
import { PAGE_STATUSES, useWorkPageDetailService } from '../hooks/useWorkPageDetailService.js';
import { getIsIncomplete } from '../utilities.js';
import { TextSelectionMenu } from './textSelectionMenu.jsx';

const notesAreaRef = React.createRef();

function WorkDetailPage({ service }) {
  const { encounterId } = useParams();
  const history = useHistory();
  const [pageState, pageApi] = useWorkPageDetailService(service, encounterId);
  const [isCompletionDialogVisible, setIsCompletionDialogVisible] = useState(false);
  const acceptRejectDialogKey = React.useId();

  if (pageState.status === PAGE_STATUSES.UNINITIALIZED) {
      return (
        <div>
          <PageStatusDialog isOpen={pageState.isRequestInProgress} statusMessage={pageState.statusMessage} />
        </div>
      );
  } else if (pageState.status === PAGE_STATUSES.ERROR) {
    return (<div>ERROR! Please refresh</div>);
  } else if (pageState.encounter === null) {
    return (
      <div>
        <PageStatusDialog isOpen={pageState.isRequestInProgress} statusMessage={pageState.statusMessage} />
      </div>
    );
  }

  const incompleteCount = pageState.encounter.lineItems.reduce((acc, curr) => {
      if (getIsIncomplete(curr.isRejected, curr.isAccepted)) {
        return acc + 1;
      }
      return acc;
    }, 0);

  const rejectedCount = pageState.encounter.lineItems.reduce((acc, curr) => {
    if (curr.isRejected) {
      return acc + 1;
    }
    return acc;
  }, 0);

  const commentCount = pageState.encounter.comments.length;

  return (
    <div>
      <Card sx={{ height: '750px' }} raised>
        <Popover selectionRef={notesAreaRef}>
          <TextSelectionMenu dateOfService={pageState.encounter.headerInfo.dateOfService} />
        </Popover>
        <CardContent>
          <Grid
            size={GRID_ROW_FULL_WIDTH_ROW}
            display="flex"
            alignItems="center"
            justifyContent="space-around"
          >
            <EncounterHeaderInfo headerInfo={pageState.encounter.headerInfo} />
          </Grid>
          <Grid size={GRID_ROW_FULL_WIDTH_ROW}>
              <Divider
                sx={{
                  paddingTop: '8px',
                  paddingBottom: '8px',
                  backgroundColor: 'rgba(0, 0, 0, 0)'
                }}
              flexItem
              >

              </Divider>
            </Grid>
          <Grid container spacing={1} sx={{ paddingBlock: '24px' }}>
            <Grid size={7} style={{ height: '550px', overflow: 'auto', paddingInline: '12px' }}>
              <EncounterNotes forwardRef={notesAreaRef} noteText={pageState.encounter.noteText} />
            </Grid>

            <Grid size={5} style={{ height: '550px', overflow: 'auto' }}>
              <Stack direction="column">
                <CollapsableSection title="Patient History" shouldStartOpened={false}>
                  <PatientHistorySection historyData={pageState.encounter.patientHistory} />
                </CollapsableSection>
                <CollapsableSection title="Line Items" shouldStartOpened={true}>
                  <EncounterLineItemList
                    encounterId={pageState.encounter.encounterId}
                    lineItems={pageState.encounter.lineItems}
                    openAcceptRejectDialog={pageApi.openAcceptRejectDialog}
                  />
                </CollapsableSection>
                <CollapsableSection title={`Comments (${pageState.encounter.comments.length})`} shouldStartOpened={false}>
                  <CommentSection
                    openAddCommentDialog={pageApi.openAddCommentDialog}
                    closeAddCommentDialog={pageApi.closeAddCommentDialog}
                    comments={pageState.encounter.comments}
                  />
                </CollapsableSection>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      <Grid container spacing={2} sx={{ marginTop: '24px' }}>
        <Grid container size={10} offset="auto" alignSelf="flex-start" justifyContent="flex-end">
          {
            incompleteCount > 0
              ? (
                <Alert color="error" severity="error">
                  Please accept or reject all line items
                </Alert>
              )
              : null
          }
          {
            (rejectedCount > 0 && commentCount < 1)
              ? (
                <Alert color="error" severity="error">
                  Please add at least one encounter comment when rejecting one or more line items
                </Alert>
              )
              : null
          }
          <Button
            fullWidth
            variant="contained"
            disableElevation
            startIcon={<Checklist />}
            disabled={(incompleteCount > 0) || (rejectedCount > 0 && commentCount < 1)}
            onClick={() => setIsCompletionDialogVisible(true)}
            sx={{ width: '256px', float: 'right' }}
          >
            Complete
          </Button>
        </Grid>
      </Grid>
      <PageStatusDialog isOpen={pageState.isRequestInProgress} statusMessage={pageState.statusMessage} />
      <CompletionDialog
        isOpen={isCompletionDialogVisible}
        handleClose={() => setIsCompletionDialogVisible(false)}
        handleCompleteEncounterReview={() => {
          return pageApi.completeEncounterReview(encounterId, history.push);
        }}
      />

      <AddCommentDialog
        isOpen={pageState.isAddCommentDialogVisible}
        handleClose={pageApi.closeAddCommentDialog}
        encounterId={pageState.encounter.encounterId}
        addCommentToEncounter={pageApi.addCommentToEncounter}
      />

      {/* only way it nukes inital value of text on dialog is to ternary null here */}
      {
        pageState.isAcceptRejectDialogVisible === true
          ? (
            <AcceptRejectDialog
              key={acceptRejectDialogKey}
              isOpen={pageState.isAcceptRejectDialogVisible}
              handleClose={pageApi.closeAcceptRejectDialog}
              lineItem={pageState.selectedLineItem}
              mode={pageState.acceptRejectDialogMode}
              handleRejectEncounterLineItem={pageApi.rejectEncounterLineItem}
              handleAcceptEncounterLineItem={pageApi.acceptEncounterLineItem}
              reasonOptions={pageState.encounter.reasonOptions}
            />
          ) :
          null
      }
    </div>
  );
}

export { WorkDetailPage };
